const orderSharp = [
  "A",
  "A#",
  "B",
  "C",
  "C#",
  "D",
  "D#",
  "E",
  "F",
  "F#",
  "G",
  "G#",
]
const orderFlat = [
  "A",
  "Bb",
  "B",
  "C",
  "Db",
  "D",
  "Eb",
  "E",
  "F",
  "Gb",
  "G",
  "Ab",
]

const structureRegex =
  /^(?<prefixDom>7)?(?<color>m|min|dim|sus|maj|add|aug|5|m7b5)?(?<extra1>2|4|6|7|9|11|13)?(?<extra2>2|4|6|7|9|11|13)?$/

const colorNotesMap: Record<string, number[]> = {
  "": [4, 7],
  "5": [7],
  m: [3, 7],
  min: [3, 7],
  m7b5: [3, 6, 10],
  dim: [3, 6],
  sus: [7],
  maj: [4, 7],
  add: [4, 7],
  aug: [4, 8],
}

const extraNotesMap: Record<string, number> = {
  "2": 2,
  "4": 5,
  "6": 9,
  "7": 10,
  "9": 14,
  "11": 17,
  "13": 20,
}

interface Chord {
  root: string
  structure: string
  bass?: string
}

export default {
  chordRegex:
    /^(?<root>[A-G][#b]?)(?<structure>(m|min|dim|sus|maj|add|aug|5|m7b5)?(2|4|6|7|9|11|13){0,2})(\/(?<bass>[A-G][#b]?))?$/,
  stringifyChord(chord: Chord) {
    let chordStr = chord.root
    if (chord.structure) {
      chordStr = chordStr + chord.structure
    }
    if (chord.bass) {
      chordStr = chordStr + "/" + chord.bass
    }
    return chordStr
  },
  parseChord(chordStr: string): Chord {
    const result = chordStr.match(this.chordRegex)
    if (!result || !result.groups) {
      console.error("Invalid chord format:", chordStr)
      return { root: "", structure: "" }
    }
    return {
      root: result.groups.root ?? "",
      structure: result.groups.structure ?? "",
      bass: result.groups.bass,
    }
  },
  transposeNote(
    note: string,
    transposeAmount: number,
    useFlats: boolean,
  ): string {
    let index = orderSharp.indexOf(note)
    if (index === -1) {
      index = orderFlat.indexOf(note)
    }
    let newIndex = (index + transposeAmount) % 12
    if (newIndex < 0) {
      newIndex = newIndex + 12
    }
    if (useFlats) {
      return orderFlat[newIndex] as string
    }
    return orderSharp[newIndex] as string
  },
  transpose(chordStr: string, transposeAmount: number, useFlats: boolean) {
    const chord = this.parseChord(chordStr)
    chord.root = this.transposeNote(chord.root, transposeAmount, useFlats) ?? ""
    if (chord.bass) {
      chord.bass = this.transposeNote(chord.bass, transposeAmount, useFlats)
    }
    return this.stringifyChord(chord)
  },
  // Turn a chord string into the notes that make up the chord
  getNotes(chordStr: string, useFlats: boolean) {
    const chord = this.parseChord(chordStr)
    const structure = chord.structure.match(structureRegex)
    // If we don't recognize the structure, just return the root
    // that should at least be correct
    if (!structure || !structure.groups) {
      return [chord.root]
    }
    // A chord consists of the root
    const notes = [0]

    // + the notes of the color (like minor, major, dim)
    notes.push(...(colorNotesMap[structure.groups.color ?? ""] ?? []))
    if (structure.groups.prefixDom) {
      notes.push(10)
    }

    // + special case for sus chords
    if (structure.groups.color === "sus" && !structure.groups.extra1) {
      notes.push(5)
    } else if (
      structure.groups.color === "sus" &&
      structure.groups.extra1 === "2"
    ) {
      notes.push(2)
    }

    // + extra notes at the end indicated by numbers
    if (structure.groups.extra1) {
      const note = extraNotesMap[structure.groups.extra1]
      if (
        note &&
        !(structure.groups.color === "sus" && structure.groups.extra1 === "2")
      )
        notes.push(note)
    }
    if (structure.groups.extra2) {
      const note = extraNotesMap[structure.groups.extra2]
      if (note) notes.push(note)
    }

    return notes.map((note) => {
      return this.transposeNote(chord.root, note, useFlats)
    })
  },
}
